import { object, string, array, number } from 'yup'
import { isUniqueObjectPropertyInArray } from '@/helpers/validation'
import {
  maxWorkSamplesCount,
  maxWorkTitlesCount,
  maxWorkWritersCount,
  WriterRolesText,
  singleArrangerError,
} from '@/constants'
import gettext from '@/gettext'

const { $gettext } = gettext

export default (trackTitle?: string) => object({
  duration: string()
    .required($gettext('Please enter a duration.'))
    .test(
      'is-time',
      $gettext('Duration must be formatted to include minutes and seconds. For example: 03:46.'),
      value => {
        if (!value) {
          return false
        }

        const timeData = value.split(':')
        const durationInSecs = (Number(timeData[0]) * 60) + Number(timeData[1])

        return /^([0-9]?[0-9]?[0-9]):[0-5][0-9]$/.test(value) && durationInSecs !== 0
      },
    ),
  alternative_titles: array().of(
    object({
      name: string()
        .max(255, $gettext('Alternative title must be less than 255 characters long.'))
        .test(
          'is-unique',
          $gettext('Duplicate Alternative Title specified.'),
          (value, context) => isUniqueObjectPropertyInArray(value, context, 'alternative_titles', 'name'),
        )
        .test(
          'is-not-match-track-title',
          $gettext('Alternative Title must be different to Work (Track) Title.'),
          value => String(trackTitle).toLowerCase().trim() !== value?.toLowerCase().trim(),
        ),
    }),
  )
    .max(maxWorkTitlesCount, $gettext('You have reached the maximum number of alternative titles for this work.')),
  writers: array().of(
    object({
      name: string()
        .required($gettext('Please enter a Writer Name.'))
        .min(2, $gettext('Writer Name must be at least 2 characters long.'))
        .max(49, $gettext('Writer Name must be less than 49 characters long.')),
      role: number()
        .required($gettext('Please fill required fields.'))
        .test(
          'type',
          $gettext('Author (Lyrics Only) can only be submitted with an accompanying Composer. Please enter the name and email address of the composer of this musical work.'),
          (value, context) => {
            if (!value || value !== WriterRolesText['Author (Lyrics only)']) {
              return true
            }

            const writers: Writer[] = context?.from?.[1].value?.writers

            return !!writers.filter(({ role }) => role !== WriterRolesText['Author (Lyrics only)']).length
          },
        ),
      share: number()
        .transform((parsedValue, originalValue) => (originalValue === '' ? null : parsedValue))
        .required($gettext('Please enter a share.'))
        .min(0.01, $gettext('Please enter a valid share.'))
        .max(100, $gettext('Please enter a valid share.')),
      email: string()
        .email($gettext('Please enter a valid email address.'))
        .max(160, $gettext('Email must be less than 160 characters long.'))
        .required($gettext('Please provide an email address for this Writer.'))
        .test(
          'is-unique',
          $gettext('The email addresses of the writers cannot be the same. Please enter different email for each writer.'),
          (value, context) => isUniqueObjectPropertyInArray(value, context, 'writers', 'email'),
        ),
    }),
  )
    .max(maxWorkWritersCount, $gettext('You have reached the maximum number of writers for this work.'))
    .test(
      'sum-of-percentage',
      $gettext('The sum of the percentages must be 100'),
      writers => {
        if (!writers?.find(({ name, role, email, share }) => name && role && email && share)) {
          return true
        }

        const sum = writers?.reduce((acc, { share }) => acc + share, 0) || 0

        return sum === 100
      },
    )
    .test(
      'does-not-have-single-arranger',
      $gettext(singleArrangerError),
      writers => !(writers?.length === 1 && writers[0].role === WriterRolesText.Arranger),
    ),
  samples: array().when('hasSamples', {
    is: true,
    then: schema => schema.of(
      object({
        writers_names: string()
          .required($gettext('Please enter a name.'))
          .min(2, $gettext('Writer Name must be at least 2 characters long.'))
          .max(255, $gettext('Writer Name must less than 255 characters long.')),
        share: number()
          .required($gettext('Please enter a share.'))
          .min(0.01, $gettext('Please enter a valid share.'))
          .max(100, $gettext('Please enter a valid share.')),
        title: string()
          .required($gettext('Please enter a title.'))
          .min(2, $gettext('Title Of Work Used must be at least 2 characters long.'))
          .max(512, $gettext('Title Of Work Used must be less than 512 characters long.'))
          .test(
            'is-unique',
            $gettext('The title of the samples used must be different.'),
            (value, context) => isUniqueObjectPropertyInArray(value, context, 'samples', 'title'),
          ),
      }),
    )
      .max(maxWorkSamplesCount, $gettext('You cannot add more than 10 samples for a musical work.'))
      .test(
        'sum-of-percentage',
        $gettext('The sum of the percentages must be less or equal 100'),
        samples => {
          const sum = samples?.reduce((acc, { share }) => acc + share, 0) || 0

          return sum <= 100
        },
      ),
  }),
})
